import { Controller } from "../../lib/stimulus-shim"

const TEMPLATE_PRIORITIES = {
  product:  ["product", "promo_code", "sponsor"],
  shop:     ["shop", "product", "affiliate"],
  sponsor:  ["sponsor", "promo_code", "product"]
}

const CATEGORY_EMOJIS = {
  product: "📦", sponsor: "🤝", promo_code: "🎁",
  shop: "🛒", affiliate: "💰", gear: "🎬"
}

const MAX_ITEMS = 3

export default class extends Controller {
  static targets = ["player", "title", "meta", "taggedItems", "description", "actions"]
  static values = {
    popupTemplate: String
  }

  open(event) {
    event.preventDefault()

    const { type, videoId, title, views, date, description, taggedItems, popupTemplate, popupActionsId,
            audioUrl, artwork, platforms, feedUrl, imageUrl, linkUrl, children, metaHtml, embedUrl } = event.params

    const modal = document.querySelector('#media-detail-modal')
    if (!modal) return

    // Contexte d'attribution : quel module/média a déclenché le visionnage.
    // Stocké sur la modale (élément global) pour la délégation de clics.
    const moduleEl = event.currentTarget.closest('[data-module-id]') || event.currentTarget.closest('[id^="module-"]')
    this._popupContext = {
      module_id: moduleEl?.dataset?.moduleId || moduleEl?.id?.replace('module-', '') || null,
      module_type: type,
      platform: type,
      media_id: videoId || audioUrl || linkUrl || null,
      media_title: title || null
    }
    modal._rhoPopupContext = this._popupContext
    this._trackPopup('media_popup_open', {})
    this._setupPopupLinkTracking(modal)

    const template = popupTemplate || this.popupTemplateValue || ""

    // 1. Inject player (vidéo YouTube/TikTok, lecteur audio ou image(s) Instagram)
    this._injectPlayer(type, videoId, { audioUrl, artwork, imageUrl, linkUrl, children, embedUrl })

    // 2. Title + meta. Instagram/TikTok : HTML rendu côté serveur (icônes via le
    // helper) ; sinon vues/date.
    this.titleTarget.textContent = title || ""
    this.metaTarget.innerHTML = (type === "instagram" || type === "tiktok")
      ? (metaHtml || "")
      : [views, date].filter(Boolean).join(" · ")

    // 2b. Actions : podcast (plateformes de l'épisode + RSS) ou template clonée (YouTube)
    if (type === "audio") {
      this._renderAudioActions(platforms, feedUrl)
    } else {
      this._renderActions(popupActionsId)
    }

    // 3. Tagged items or description
    const items = this._toArray(taggedItems)
    const sortedItems = this._sortByTemplate(items, template)

    if (sortedItems.length > 0 && template) {
      this.taggedItemsTarget.innerHTML = this._renderItems(sortedItems, template)
      this.taggedItemsTarget.classList.remove("hidden")
      this.descriptionTarget.classList.add("hidden")
    } else if (description) {
      this.descriptionTarget.textContent = description
      this.descriptionTarget.classList.remove("hidden")
      this.taggedItemsTarget.classList.add("hidden")
    } else {
      this.taggedItemsTarget.classList.add("hidden")
      this.descriptionTarget.classList.add("hidden")
    }

    modal.showModal()
  }

  // Délégation UNIQUE sur la modale : tout <a> présent ou futur est tracké,
  // quel que soit le template de plateforme. Les liens qui portent déjà le
  // deep-link-tracker s'auto-trackent (on ne double-compte pas).
  _setupPopupLinkTracking(modal) {
    if (modal._rhoPopupTracked) return
    modal._rhoPopupTracked = true

    modal.addEventListener('click', (e) => {
      const link = e.target.closest('a[href]')
      if (!link || !modal.contains(link)) return
      if ((link.dataset.controller || '').includes('deep-link-tracker')) return

      const ctx = modal._rhoPopupContext || {}
      this._trackPopup('link_click', {
        link_url: link.href,
        link_text: link.textContent.trim().slice(0, 80),
        module_id: ctx.module_id,
        module_type: ctx.module_type,
        source: 'media_popup'
      })
    })
  }

  _trackPopup(eventName, properties) {
    if (typeof ahoy === 'undefined') return
    if (document.querySelector('meta[name="rho-owner"]')) return

    const ctx = this._popupContext || {}
    ahoy.track(eventName, {
      profile_id: document.querySelector('[data-profile-id]')?.dataset.profileId,
      page_id: document.querySelector('[data-page-id]')?.dataset.pageId,
      ...ctx,
      ...properties
    })
  }

  close() {
    const modal = document.querySelector('#media-detail-modal')
    if (!modal) return

    this.playerTarget.innerHTML = ""
    if (this.hasActionsTarget) this.actionsTarget.innerHTML = ""
    modal.close()
  }

  copyCode(event) {
    const code = event.currentTarget.dataset.code
    if (!code) return

    navigator.clipboard.writeText(code).then(() => {
      const btn = event.currentTarget
      const original = btn.textContent
      btn.textContent = "Copié !"
      setTimeout(() => { btn.textContent = original }, 2000)
    })
  }


  // Clone la <template> d'actions fournie par le module source.
  // Stimulus connecte automatiquement les contrôleurs du markup injecté.
  _renderActions(templateId) {
    if (!this.hasActionsTarget) return

    this.actionsTarget.innerHTML = ""
    const template = templateId ? document.getElementById(templateId) : null

    if (template?.content) {
      this.actionsTarget.appendChild(template.content.cloneNode(true))
      this.actionsTarget.classList.remove("hidden")
    } else {
      this.actionsTarget.classList.add("hidden")
    }
  }

  _injectPlayer(type, videoId, { audioUrl, artwork, imageUrl, linkUrl, children, embedUrl } = {}) {
    switch (type) {
      case "tiktok": {
        // TikTok fournit un embed_link → iframe vertical. Fallback : cover + lien.
        this.playerTarget.classList.remove("aspect-video", "bg-black")
        if (embedUrl) {
          this.playerTarget.innerHTML = `
            <div class="flex justify-center bg-base-200 w-full">
              <iframe src="${this._escapeAttr(embedUrl)}" class="w-full max-w-sm h-[70vh]"
                frameborder="0" allow="autoplay; encrypted-media; fullscreen" allowfullscreen></iframe>
            </div>`
        } else {
          const link = linkUrl
            ? `<a href="${this._escapeAttr(linkUrl)}" target="_blank" rel="noopener noreferrer" class="btn btn-primary btn-sm mt-3">Voir sur TikTok</a>`
            : ""
          this.playerTarget.innerHTML = `<div class="flex flex-col items-center p-2 w-full">
            <img src="${this._escapeAttr(imageUrl)}" alt="" class="max-h-[65vh] w-auto object-contain bg-base-200" />${link}</div>`
        }
        break
      }
      case "instagram": {
        // Pas d'embed vidéo IG : on affiche l'image (ou la galerie d'un album)
        // + un lien « Voir sur Instagram » (rétention/abonnement).
        this.playerTarget.classList.remove("aspect-video", "bg-black")
        const imgs = this._toArray(children)
        const media = imgs.length > 1
          ? `<div class="flex gap-1 overflow-x-auto snap-x w-full">${
              imgs.map(u => `<img src="${this._escapeAttr(u)}" alt="" class="h-[60vh] w-auto object-cover snap-center rounded" />`).join("")
            }</div>`
          : `<img src="${this._escapeAttr(imageUrl)}" alt="" class="w-full max-h-[65vh] object-contain bg-base-200" />`
        const link = linkUrl
          ? `<a href="${this._escapeAttr(linkUrl)}" target="_blank" rel="noopener noreferrer" class="btn btn-primary btn-sm mt-3">Voir sur Instagram</a>`
          : ""
        this.playerTarget.innerHTML = `<div class="flex flex-col items-center p-2 w-full">${media}${link}</div>`
        break
      }
      case "youtube":
        this.playerTarget.classList.add("aspect-video", "bg-black")
        this.playerTarget.innerHTML = `
          <iframe width="100%" height="100%"
            src="https://www.youtube-nocookie.com/embed/${videoId}?autoplay=1&playsinline=1&rel=0"
            frameborder="0"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
            allowfullscreen></iframe>`
        break
      case "audio":
        // Pas de vidéo : artwork + lecteur audio natif
        this.playerTarget.classList.remove("aspect-video", "bg-black")
        this.playerTarget.innerHTML = `
          <div class="flex flex-col items-center gap-4 p-6 bg-base-200">
            ${artwork ? `<img src="${this._escapeAttr(artwork)}" alt="" class="w-40 h-40 rounded-xl object-cover shadow-lg" />` : ""}
            <audio controls autoplay class="w-full" src="${this._escapeAttr(audioUrl)}"></audio>
          </div>`
        break
      default:
        this.playerTarget.innerHTML = ""
    }
  }

  // Actions podcast : boutons des plateformes de l'épisode + copier le flux RSS
  _renderAudioActions(platformsParam, feedUrl) {
    if (!this.hasActionsTarget) return

    const platforms = this._toArray(platformsParam)

    // Boutons plateformes en style primary (cohérent avec YouTube et le reste
    // des CTA) ; le « copier le flux RSS » reste une action secondaire (outline).
    const buttons = platforms
      .filter((p) => p && p.url)
      .map((p) => `
        <a href="${this._escapeAttr(p.url)}" target="_blank" rel="noopener noreferrer"
           class="btn btn-sm btn-primary gap-2"
           data-controller="shared--deep-link-tracker"
           data-action="click->shared--deep-link-tracker#trackClick"
           data-shared--deep-link-tracker-web-url-value="${this._escapeAttr(p.url)}"
           data-shared--deep-link-tracker-platform-value="${this._escapeAttr((p.name || "audio").toLowerCase())}"
           data-shared--deep-link-tracker-module-id-value="${this._escapeAttr(this._popupContext?.module_id || "")}"
           data-shared--deep-link-tracker-module-type-value="audio">
          ${this._escapeHtml(p.name || "Écouter")}
        </a>`)
      .join("")

    const rss = feedUrl
      ? `<button type="button" class="btn btn-sm btn-outline gap-2"
                 data-action="click->profiles--media-detail#copyFeed"
                 data-feed="${this._escapeAttr(feedUrl)}">
           Copier le flux RSS
         </button>`
      : ""

    if (!buttons && !rss) {
      this.actionsTarget.classList.add("hidden")
      return
    }

    this.actionsTarget.innerHTML = `<div class="flex flex-wrap items-center justify-center gap-2">${buttons}${rss}</div>`
    this.actionsTarget.classList.remove("hidden")
  }

  copyFeed(event) {
    const url = event.currentTarget.dataset.feed
    if (!url) return

    navigator.clipboard.writeText(url).then(() => {
      const btn = event.currentTarget
      const original = btn.textContent
      btn.textContent = "Copié !"
      setTimeout(() => { btn.textContent = original }, 2000)
    })
  }

  _escapeAttr(str) {
    return (str || "").replace(/"/g, "&quot;")
  }

  // Les action params JSON sont auto-parsés par Stimulus (déjà un Array).
  // On reste défensif : Array tel quel, string JSON parsée, sinon [].
  _toArray(value) {
    if (Array.isArray(value)) return value
    if (typeof value === "string" && value.trim()) {
      try { return JSON.parse(value) } catch (e) { return [] }
    }
    return []
  }

  _sortByTemplate(items, template) {
    const priorities = TEMPLATE_PRIORITIES[template]
    if (!priorities) return items.slice(0, MAX_ITEMS)

    const sorted = []
    for (const category of priorities) {
      const match = items.find(i => i.category === category && !sorted.includes(i))
      if (match) sorted.push(match)
      if (sorted.length >= MAX_ITEMS) break
    }
    for (const item of items) {
      if (sorted.length >= MAX_ITEMS) break
      if (!sorted.includes(item)) sorted.push(item)
    }
    return sorted
  }

  _renderItems(items, template) {
    return items.map((item, index) => {
      if (index === 0) return this._renderHero(item, template)
      if (item.category === "promo_code") return this._renderPromo(item)
      return this._renderSecondary(item)
    }).join("")
  }

  _renderHero(item, template) {
    const emoji = CATEGORY_EMOJIS[item.category] || ""

    if (template === "shop" && item.url) {
      return `<div class="space-y-2">
        <a href="${this._escapeHtml(item.url)}" target="_blank" rel="noopener"
           class="flex items-center justify-between p-2 bg-base-200 rounded-lg hover:bg-base-300">
          ${emoji} ${this._escapeHtml(item.content)}
          <span class="text-primary text-sm">Voir &rarr;</span>
        </a>
      </div>`
    }

    if (template === "sponsor") {
      return `<div class="p-3 bg-base-200 rounded-lg">
        <p class="font-semibold">${emoji} ${this._escapeHtml(item.content)}</p>
      </div>`
    }

    if (item.url) {
      return `<a href="${this._escapeHtml(item.url)}" target="_blank" rel="noopener"
         class="btn btn-primary w-full justify-between">
        ${emoji} ${this._escapeHtml(item.content)} <span>Voir &rarr;</span>
      </a>`
    }
    return `<div class="p-3 bg-base-200 rounded-lg font-semibold">
      ${emoji} ${this._escapeHtml(item.content)}
    </div>`
  }

  _renderPromo(item) {
    const code = item.promo_code || ""
    const discount = item.discount || ""
    const label = [discount, code ? `avec <strong>${this._escapeHtml(code)}</strong>` : ""].filter(Boolean).join(" ")

    return `<div class="flex items-center justify-between p-2 bg-base-200 rounded-lg">
      <span>🎁 ${label}</span>
      ${code ? `<button class="btn btn-xs btn-ghost" data-action="click->profiles--media-detail#copyCode" data-code="${this._escapeHtml(code)}">Copier</button>` : ""}
    </div>`
  }

  _renderSecondary(item) {
    const emoji = CATEGORY_EMOJIS[item.category] || ""

    if (item.url) {
      return `<a href="${this._escapeHtml(item.url)}" target="_blank" rel="noopener"
         class="flex items-center justify-between p-2 bg-base-200 rounded-lg hover:bg-base-300">
        ${emoji} ${this._escapeHtml(item.content)}
        <span class="text-primary text-sm">Voir &rarr;</span>
      </a>`
    }
    return `<p class="text-sm text-base-content/60">${emoji} ${this._escapeHtml(item.content)}</p>`
  }

  _escapeHtml(str) {
    if (!str) return ""
    const div = document.createElement("div")
    div.textContent = str
    return div.innerHTML
  }
}
